import {useQueries,useQuery} from '@tanstack/react-query'
import {useNavigate, useRouter} from '@tanstack/react-router'
import Big from 'big.js'
import {format} from 'date-fns'

import DeliveryTruckIcon from '@/assets/food/icons/delivery-truck.svg?react'
import Loading from '@/components/Loading/Loading'
import Price from '@/components/Price'
import PrimaryButton from '@/components/PrimaryButton/PrimaryButton'
import {USD_DISPLAY_DECIMALS} from '@/constants/config'
import Pagination from '@/layout/Pagination'
import type {FoodProduct,Order} from '@/lib/food/foodModel'
import {getFoodOrders} from '@/lib/food/foodPaymentService'
import {getFoodProductDetails} from '@/lib/food/foodService'
import useStoreDetails from '@/lib/food/hooks/useStoreDetails'
import HeadTags from '@/lib/head/HeadTags'
import {useLocale} from '@/lib/locale/locale'
import {FoodOrderRoute, FoodOrdersRoute, FoodTrackingRoute} from '@/routeRegistry'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'
import useWindowSize from '@/utils/hooks/useWindowSize'
import {NO_REFETCH_OPTIONS} from '@/utils/query/constants'
import useSearch from '@/utils/router/useSearch'

import style from './FoodOrders.module.scss'
import MobileItemLayout from './shared/MobileItemLayout'
import type {Customization, FoodItem} from './shared/types'
import {getCustomizationText} from './shared/utils'

const ITEMS_PER_PAGE = 10
const ITEMS_PER_PAGE_MOBILE = 10

// Render single food product with image and customizations
function FoodItemDisplay({ item, productDetails }: { 
  item: FoodItem
  productDetails?: FoodProduct | null 
}) {
  const imageUrl: string | null | undefined = productDetails?.image ?? productDetails?.thumbnail_image
  const customizations: string = getCustomizationText((item.customizations ?? []) as Customization[])
  
  return (
    <div className={style.FoodItem}>
      <div className={style.FoodItemImage}>
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={item.name}
            loading="lazy"
            onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
              const target = e.target as HTMLImageElement
              target.style.display = 'none'
              const placeholder = target.parentElement?.querySelector(`.${style.FoodItemImagePlaceholder}`)
              if (placeholder) (placeholder as HTMLElement).style.display = 'flex'
            }}
          />
        ) : null}
        <div 
          className={style.FoodItemImagePlaceholder}
          style={{ display: imageUrl ? 'none' : 'flex' }}
          aria-hidden="true"
        >
          🍽️
        </div>
      </div>
      <div className={style.FoodItemDetails}>
        <div className={style.FoodItemName}>{item.name}</div>
        {customizations && (
          <div className={style.FoodItemNote}>{customizations}</div>
        )}
      </div>
    </div>
  )
}

// Render complete order card with all details and food products
function FoodOrderItem({ order, productDetailsMap }: {
  order: Order
  productDetailsMap: Map<string, FoodProduct | null>
}) {
  const navigate = useNavigate()
  const {data: storeDetails} = useStoreDetails(order.extras.storeId)
  
  const subtotal = Big(order.quote.subtotal)
  const deliveryFee = Big(order.quote.deliveryFee)
  const serviceFee = Big(order.quote.serviceFee)
  const salesTax = Big(order.quote.salesTax)
  const total = Big(order.quote.totalWithoutTips)
  
  const formatDate = (dateString: string, pattern: string) => {
    try {
      const date = new Date(dateString)
      return isNaN(date.getTime()) ? 'Invalid date' : format(date, pattern)
    } catch {
      return 'Invalid date'
    }
  }
  
  // Determine if order is completed
  const isCompleted = order.status.toLowerCase() === 'delivered' || 
                     order.status.toLowerCase() === 'completed'

  // Render store icon
  const hasStoreLogo = storeDetails?.logo_photos && storeDetails.logo_photos.length > 0
  const storeIcon = hasStoreLogo ? (
    <img 
      src={storeDetails.logo_photos[0]} 
      alt=""
      aria-hidden="true"
      className={style.StoreLogoImage}
      loading="lazy"
    />
  ) : (
    <span aria-hidden="true">🍴</span>
  )

  return (
    <div className={style.Order}>
      {/* Render order header */}
      <div className={style.OrderDetails}>
        <div className={style.Header}>
          <div>
            <h2 className={style.OrderId}>Order #{order.id}</h2>
            <div className={style.Time}>
              Placed on {formatDate(order.createdAt, 'MMM dd, yyyy')}
            </div>
          </div>
          <div className={style.TotalPrice}>
            <h3>Total price</h3>
            <div>
              <Price price={total} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
            </div>
          </div>
          <div className={style.ShippingInfo}>
            <VisuallyHidden as='h3'>Ship to</VisuallyHidden>
            <div className={style.Name}>
              <span aria-hidden>Ship to: </span>
              <VisuallyHidden>Name: </VisuallyHidden>
              {order.shippingInfo.name}
            </div>
            <div className={style.Address}>
              <VisuallyHidden>Address: {order.shippingInfo.line1}, {order.shippingInfo.city}, {order.shippingInfo.state}, {order.shippingInfo.postalCode}, {order.shippingInfo.country}</VisuallyHidden>
              <div className={style.AddressItem} aria-hidden="true">{order.shippingInfo.line1}</div>
              <div className={style.AddressItem} aria-hidden="true">
                {order.shippingInfo.city}, {order.shippingInfo.state} {order.shippingInfo.postalCode}
              </div>
              <div className={style.AddressItem} aria-hidden="true">{order.shippingInfo.country}</div>
            </div>
          </div>
        </div>
        <div className={style.Divider} />
        
        {/* Display store information */}
        <div className={style.StoreInfo}>
          <div className={style.StoreIcon}>
            {storeIcon}
          </div>
          <div className={style.StoreName}>{order.extras.store}</div>
        </div>
        
        {/* Render food items list */}
        <div className={style.FoodItems}>
          <VisuallyHidden as='h3'>Food items ({order.foodItems.length} items)</VisuallyHidden>
          
          <div className={style.ItemsHeader}>
            <div className={style.ItemsHeaderProduct}>
              All products ({order.foodItems.length} {order.foodItems.length === 1 ? 'product' : 'products'})
            </div>
            <div className={style.ItemsHeaderQuantity}>Quantity</div>
            <div className={style.ItemsHeaderPrice}>Price</div>
          </div>
          
          <div className={style.ItemsList}>
            {order.foodItems.map((item, index) => (
              <div key={index} className={style.ItemRow}>
                {/* Mobile layout structure */}
                <MobileItemLayout
                  item={item}
                  productDetails={productDetailsMap.get(item.productId)}
                  styles={{
                    MobileItemHeader: style.MobileItemHeader,
                    MobileItemImage: style.MobileItemImage,
                    MobileItemImagePlaceholder: style.MobileItemImagePlaceholder,
                    MobileItemContent: style.MobileItemContent,
                    MobileItemNameAndQuantity: style.MobileItemNameAndQuantity,
                    MobileItemName: style.MobileItemName,
                    MobileItemQuantity: style.MobileItemQuantity,
                    MobileItemDetails: style.MobileItemDetails,
                    MobileItemPrice: style.MobileItemPrice,
                    MobileItemCustomizations: style.MobileItemCustomizations,
                  }}
                />

                {/* Desktop layout structure */}
                <div className={style.ItemProduct}>
                  <FoodItemDisplay 
                    item={item} 
                    productDetails={productDetailsMap.get(item.productId)} 
                  />
                </div>
                <div className={style.ItemQuantity}>{item.quantity}</div>
                <div className={style.ItemPrice}>
                  <Price 
                    price={Big(item.basePrice)} 
                    options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      <div className={style.Divider} />
      
      {/* Display delivery status */}
      <div className={style.DeliveryInfo}>
        <div className={style.DeliveryStatusContainer}>
          <div className={style.DeliveryStatusLeft}>
            {isCompleted ? (
              <>
                <div className={style.Header}>
                  <h2 className={style.DeliveryHeaderText}>
                    Delivered on {formatDate(order.updatedAt || order.createdAt, 'EEE, MMM d h:mm a')}
                  </h2>
                </div>
                <div className={style.DeliveryStatus}>
                  <div className={style.StatusBadge}>
                    <span className={style.BadgeText}>Delivered</span>
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className={style.DeliveryStatusActive}>
                  <div className={style.DeliveryTime}>
                    Arrives by {formatDate(
                      new Date(new Date(order.createdAt).getTime() + order.quote.deliveryTimeMax * 60 * 1000).toISOString(),
                      'MMM d h:mm a'
                    )}
                  </div>
                </div>
                <div className={style.DeliveryStatus}>
                  <div className={style.StatusBadge}>
                    <DeliveryTruckIcon className={style.TruckIcon} aria-hidden="true" />
                    <span className={style.BadgeText}>Out for delivery</span>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className={style.DeliveryStatusRight}>
            <PrimaryButton
              size='lg'
              className={isCompleted ? style.ViewDetailButton : style.TrackOrderButton}
              onPress={() => {
                if (isCompleted) {
                  void navigate({ to: FoodOrderRoute.fullPath, params: { foodOrderId: order.id } })
                } else {
                  void navigate({ to: FoodTrackingRoute.fullPath, params: { foodOrderId: order.id } })
                }
              }}
            >
              {isCompleted ? 'View details' : 'Track your delivery order'}
            </PrimaryButton>
          </div>
        </div>
      </div>
      
      <div className={style.Divider} />
      
      {/* Display cost breakdown */}
      <div className={style.DeliveryInfo}>
        <div className={style.DeliveryItems}>
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>
              Subtotal ({order.foodItems.length} {order.foodItems.length === 1 ? 'item' : 'items'})
              <VisuallyHidden>:</VisuallyHidden>
            </div>
            <div className={style.DeliveryItemPrice}>
              <Price price={subtotal} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
            </div>
          </div>
          {deliveryFee.gt(0) && (
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>Delivery Fee<VisuallyHidden>:</VisuallyHidden></div>
              <div className={style.DeliveryItemPrice}>
                <Price price={deliveryFee} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
              </div>
            </div>
          )}
          {serviceFee.gt(0) && (
            <div className={style.DeliveryItem}>
              <div className={style.DeliveryItemName}>Service Fee<VisuallyHidden>:</VisuallyHidden></div>
              <div className={style.DeliveryItemPrice}>
                <Price price={serviceFee} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
              </div>
            </div>
          )}
          <div className={style.DeliveryItem}>
            <div className={style.DeliveryItemName}>Tax<VisuallyHidden>:</VisuallyHidden></div>
            <div className={style.DeliveryItemPrice}>
              <Price price={salesTax} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
            </div>
          </div>
        </div>
      </div>
      
      {/* Display order total */}
      <div className={style.Total}>
        <VisuallyHidden>Total: ${total.toFixed(2)}</VisuallyHidden>
        <span aria-hidden="true">Total<VisuallyHidden>:</VisuallyHidden></span>
        <span aria-hidden="true">
          <Price price={total} options={{ fractionDigits: USD_DISPLAY_DECIMALS, exactFractionDigits: true }} />
        </span>
      </div>
    </div>
  )
}

// Render main component
export default memo(function FoodOrders() {
  const [locale] = useLocale()
  const router = useRouter()
  const {isMobile} = useWindowSize()
  
  // Fetch food orders data
  const {data: orders = [], isLoading} = useQuery({
    queryKey: ['food-orders', locale],
    queryFn: async ({signal}) => getFoodOrders({signal, locale}),
    ...NO_REFETCH_OPTIONS,
  })
  
  // Extract unique product IDs for image fetching
  const productIds = useMemo(() => {
    const ids = new Set<string>()
    orders.forEach(order => {
      order.foodItems.forEach(item => {
        if (item.productId) ids.add(item.productId)
      })
    })
    return Array.from(ids)
  }, [orders])
  
  // Fetch product details for food images
  const productQueries = useQueries({
    queries: productIds.map(productId => ({
      queryKey: ['food-product-details', productId],
      queryFn: async ({signal}: {signal?: AbortSignal}) => {
        try {
          return await getFoodProductDetails({foodProductId: productId, signal})
        } catch {
          return null
        }
      },
      staleTime: 1000 * 60 * 5,
      enabled: !!productId,
      retry: false,
      refetchOnWindowFocus: false,
    })),
  })
  
  const productDetailsMap = useMemo(() => {
    const map = new Map<string, FoodProduct | null>()
    productIds.forEach((productId, index) => {
      const queryResult = productQueries[index]
      if (queryResult?.data) {
        map.set(productId, queryResult.data)
      }
    })
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps -- useQueries result changes every render, but we only need to rebuild when productIds change
  }, [productIds])
  
  // Handle pagination state
  const [page, setPage] = useSearch(FoodOrdersRoute.id, 'page', {
    replace: false,
    resetScroll: true,
    hashScrollIntoView: { behavior: 'smooth', block: 'start', inline: 'nearest' },
  })
  
  // Calculate pagination values
  const itemsPerPage = isMobile ? ITEMS_PER_PAGE_MOBILE : ITEMS_PER_PAGE
  const totalOrders = orders.length
  const pageCount = totalOrders > 0 ? Math.ceil(totalOrders / itemsPerPage) : 1
  const startIndex = (page - 1) * itemsPerPage
  const currentPageItems = orders.slice(startIndex, startIndex + itemsPerPage)
  
  // Auto-correct page if out of bounds
  useEffect(() => {
    if (orders.length && pageCount > 0 && page > pageCount) {
      void setPage(pageCount)
    }
  }, [setPage, page, pageCount, orders.length])
  
  const handlePageChange = useCallback((event: {selected: number}) => {
    void setPage(event.selected + 1)
  }, [setPage])
  
  const hrefBuilder = useCallback((pageNum: number) =>
    router.buildLocation({ to: FoodOrdersRoute.fullPath, search: {page: pageNum} }).href,
    [router]
  )

  return (
    <>
      <HeadTags title={`Food Orders (${totalOrders})`} />
      <main className={style.OrdersSection}>
        <h1 className={style.Heading}>Food Orders ({totalOrders})</h1>
        {isLoading && <Loading label='Loading food orders...' />}
        {!isLoading && totalOrders === 0 && `No food orders.`}
        {totalOrders > 0 && (
          <div className={style.Orders}>
            {currentPageItems.map(order => (
              <FoodOrderItem 
                key={order.id} 
                order={order}
                productDetailsMap={productDetailsMap}
              />
            ))}
          </div>
        )}
        {pageCount > 1 && (
          <Pagination
            onPageChange={handlePageChange}
            pageCount={pageCount}
            currentPage={page - 1}
            hrefBuilder={hrefBuilder}
          />
        )}
      </main>
    </>
  )
}) 